import { z } from 'zod';

// Environment validation schema
const envSchema = z.object({
  // Python LLM API (SSE Chat)
  VITE_MAS_BASE_URL: z
    .string()
    .refine((val) => val === '' || z.string().url().safeParse(val).success, {
      message: 'Invalid API URL',
    }),

  // Spring Boot API (Auth, Products, Orders, etc.)
  VITE_SERVICE_BASE_URL: z.string().url('Invalid Spring Boot API URL').optional(),

  // Remove WebSocket URL - no longer needed
  // VITE_WS_URL: z.string().url('Invalid WebSocket URL').optional(),

  // App Configuration
  VITE_NODE_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_DATA_POLICY_BASE_URL: z.string().url('Invalid Data Policy URL').optional(),
  VITE_DATA_POLICY_MAIL: z.string().email('Invalid Data Policy Mail').optional(),
});

// Validate and parse environment variables
export function validateEnv() {
  try {
    console.log('Validating environment...', import.meta.env);
    const rawEnv = {
      VITE_MAS_BASE_URL: import.meta.env.VITE_MAS_BASE_URL, // Python LLM backend
      VITE_SERVICE_BASE_URL: import.meta.env.VITE_SERVICE_BASE_URL, // Spring Boot backend
      VITE_NODE_ENV: import.meta.env.VITE_NODE_ENV,
      VITE_DATA_POLICY_BASE_URL: import.meta.env.VITE_DATA_POLICY_BASE_URL,
      VITE_DATA_POLICY_MAIL: import.meta.env.VITE_DATA_POLICY_MAIL,
    };

    const parsed = envSchema.parse(rawEnv);

    const errors: string[] = [];

    // API URL validation - allow empty string for development proxy
    if (rawEnv.VITE_MAS_BASE_URL && rawEnv.VITE_MAS_BASE_URL.trim() !== '') {
      try {
        new URL(rawEnv.VITE_MAS_BASE_URL);
      } catch {
        errors.push('VITE_MAS_BASE_URL: Invalid API URL');
      }
    }

    if (errors.length > 0) {
      console.error('Environment validation failed:', errors.join('\n  '));
      throw new Error('Invalid environment configuration');
    }

    return {
      VITE_MAS_BASE_URL: parsed.VITE_MAS_BASE_URL || '', // Python LLM backend
      VITE_SERVICE_BASE_URL: parsed.VITE_SERVICE_BASE_URL || 'http://localhost:8080', // Spring Boot
      VITE_NODE_ENV: parsed.VITE_NODE_ENV,
      VITE_DATA_POLICY_BASE_URL: parsed.VITE_DATA_POLICY_BASE_URL,
      VITE_DATA_POLICY_MAIL: parsed.VITE_DATA_POLICY_MAIL,
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('Environment validation failed:');
      error.issues.forEach((issue) => {
        console.error(`  ${issue.path.join('.')}: ${issue.message}`);
      });
      throw new Error('Invalid environment configuration');
    }
    throw error;
  }
}

// Create typed environment object
export const env = validateEnv();

// Runtime environment checks
export const isDevelopment = env.VITE_NODE_ENV === 'development';
export const isProduction = env.VITE_NODE_ENV === 'production';

// API configuration
export const apiConfig = {
  baseURL: env.VITE_SERVICE_BASE_URL,
  timeout: isDevelopment ? 10000 : 5000,
  retries: isProduction ? 3 : 1,
} as const;

// Validate environment on module load
if (typeof window !== 'undefined') {
  try {
    validateEnv();
  } catch (error) {
    console.error('Environment validation failed:', error);
  }
}
